import { LitElement, css, html } from 'lit';
import { customElement, property } from 'lit/decorators.js';

/**
 * <embrace-chat-panel> — het paneel-chrome (container + animatie + layout).
 *
 * Het panel is een generieke container met 3 slots:
 *   - header — full-bleed gradient, klant-configureerbaar via slotted content
 *   - body   — hoofdinhoud (scrollt intern indien nodig)
 *   - footer — composer of ended-banner, blijft onderaan
 */
@customElement('embrace-chat-panel')
export class EmbraceChatPanel extends LitElement {
  @property({ type: Boolean, reflect: true })
  open = false;

  static override styles = css`
    :host {
      display: contents;
    }

    .panel {
      position: fixed;
      right: var(--ec-offset-x);
      bottom: calc(var(--ec-offset-y) + var(--ec-launcher-size) + 16px);
      z-index: calc(var(--ec-z-index) - 1);

      width: var(--ec-panel-width);
      height: var(--ec-panel-max-height);
      max-width: calc(100vw - 2 * var(--ec-offset-x));
      max-height: calc(100vh - 120px);

      background: var(--ec-panel-bg);
      color: var(--ec-text);
      border-radius: var(--ec-radius-panel);
      box-shadow: var(--ec-shadow-panel);
      overflow: hidden;

      display: grid;
      /* minmax(0, …) i.p.v. 1fr: voorkomt dat slotted content met
         grote min-content (max-content avatar-strip e.d.) de track
         opduwt voorbij de panel-breedte. */
      grid-template-columns: minmax(0, 1fr);
      grid-template-rows: auto 1fr auto;
      grid-template-areas:
        "header"
        "body"
        "footer";

      opacity: 0;
      transform: translateY(12px) scale(.98);
      transform-origin: bottom right;
      pointer-events: none;
      transition: var(--ec-transition-panel-close);
    }

    .header-slot { grid-area: header; }
    .body-slot   { grid-area: body; }
    .footer-slot { grid-area: footer; }

    :host([open]) .panel {
      opacity: 1;
      transform: translateY(0) scale(1);
      pointer-events: auto;
      transition: var(--ec-transition-panel-open);
    }

    @media (max-width: 480px) {
      /* Op mobiel is het paneel altijd full-screen. */
      .panel {
        position: fixed;
        inset: 0;
        right: 0;
        bottom: 0;
        width: 100vw;
        max-width: none;
        max-height: 100vh;
        height: 100vh;
        border-radius: 0;
      }
    }

    .header-slot {
      background: var(--ec-header-bg);
      color: var(--ec-on-primary);
    }

    .body-slot {
      min-height: 0;
      display: flex;
      flex-direction: column;
      overflow: hidden;
      background: var(--ec-background);
    }

    /* Slotted descendants: laat ze zelf hun layout bepalen */
    ::slotted([part="message-list"]) {
      flex: 1;
      min-height: 0;
    }
    ::slotted(.status-hero) {
      flex: 1;
    }
  `;

  override render() {
    return html`
      <div part="panel" class="panel" role="dialog" aria-label="Klantenservice">
        <div class="header-slot">
          <slot name="header"></slot>
        </div>
        <div class="body-slot">
          <slot></slot>
        </div>
        <div class="footer-slot">
          <slot name="footer"></slot>
        </div>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'embrace-chat-panel': EmbraceChatPanel;
  }
}
